import React from 'react';
import { ExternalLink, Mail, Phone, MapPin, GraduationCap, Wallet, Clock, Calendar, ShieldCheck, ShieldAlert } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { TRACKS } from '@/data/flow';
import { formatIDR } from '@/lib/storage';

const trackLabel = (v) => TRACKS.find((t) => t.value === v)?.label || v || '—';
const SOCIAL_LABELS = { instagram: 'Instagram', linkedin: 'LinkedIn', github: 'GitHub', twitter: 'X' };

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('id-ID', { dateStyle: 'medium', timeStyle: 'short' });
  } catch { return iso; }
};

const Field = ({ label, children }) => (
  <div className="py-2">
    <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
    <div className="mt-1 text-sm text-foreground">{children || <span className="text-muted-foreground">—</span>}</div>
  </div>
);

export const ApplicantDetail = ({ record, open, onClose }) => {
  if (!record) return null;
  const a = record.answers || {};
  const edu = a.education;
  const socials = Object.entries(a.social || {}).filter(([, v]) => v && v.trim());
  const portfolio = a.portfolio || [];
  const verified = record.meta?.turnstileVerified;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[88vh] max-w-2xl overflow-y-auto scrollbar-slim">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Detail Pelamar</DialogTitle>
        </DialogHeader>

        <div className="flex items-start gap-4">
          {record.photo?.url ? (
            <img src={record.photo.url} alt={a.fullName} className="h-24 w-24 shrink-0 rounded-2xl object-cover ring-1 ring-border" />
          ) : (
            <div className="grid h-24 w-24 shrink-0 place-items-center rounded-2xl bg-secondary text-2xl font-semibold text-muted-foreground">
              {(a.fullName || '?').charAt(0)}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <h3 className="font-display text-lg font-bold text-foreground">{a.fullName || '—'}</h3>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <Badge className="rounded-full bg-primary-soft text-primary hover:bg-primary-soft">{trackLabel(a.track)}</Badge>
              <span className="font-mono text-xs text-muted-foreground">{record.id}</span>
            </div>
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-1"><Mail className="h-3.5 w-3.5" /> {a.email || '—'}</span>
              <span className="inline-flex items-center gap-1"><Phone className="h-3.5 w-3.5" /> {a.phone || '—'}</span>
              <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {a.city || '—'}</span>
            </div>
          </div>
        </div>

        <div className="mt-2 grid grid-cols-1 gap-x-6 sm:grid-cols-2">
          <Field label="Pendidikan saat ini / terakhir">
            {edu?.level ? (
              <span className="inline-flex items-center gap-1.5"><GraduationCap className="h-4 w-4 text-primary" />{`${edu.level} · ${edu.major} · ${edu.institution}${edu.year ? ` (${edu.year})` : ''}`}</span>
            ) : null}
          </Field>
          <Field label="Durasi magang">
            {a.duration ? <span className="inline-flex items-center gap-1.5"><Clock className="h-4 w-4 text-primary" />{a.duration}</span> : null}
          </Field>
          <Field label="Ekspektasi uang saku">
            <span className="inline-flex items-center gap-1.5"><Wallet className="h-4 w-4 text-primary" />{a.salary === null || a.salary === undefined ? 'Fleksibel' : `${formatIDR(a.salary)} / bulan`}</span>
          </Field>
          <Field label="Media sosial">
            {socials.length ? socials.map(([k, v]) => <div key={k}>{SOCIAL_LABELS[k] || k}: {v}</div>) : null}
          </Field>
        </div>

        <Field label="Skill">
          {a.skills?.length ? (
            <div className="flex flex-wrap gap-1.5">
              {a.skills.map((s) => <Badge key={s} variant="secondary" className="rounded-full bg-secondary">{s}</Badge>)}
            </div>
          ) : null}
        </Field>
        <Field label="Pengetahuan bidang">{a.positionKnowledge}</Field>

        {a.skillTest?.length > 0 && (
          <div className="py-2">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Tes skill (direview HRD)</p>
            <div className="mt-2 space-y-3">
              {a.skillTest.map((t, i) => (
                <div key={i} className="rounded-xl border border-border bg-secondary/30 p-3">
                  <p className="text-sm font-medium text-foreground">{i + 1}. {t.question}</p>
                  <p className="mt-1 whitespace-pre-wrap text-sm text-muted-foreground">{t.answer || '—'}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        <Field label="Pengalaman / proyek">{a.experience}</Field>
        <Field label="Portofolio & lampiran">
          {portfolio.length ? (
            <div className="space-y-1">
              {portfolio.map((l) => (
                <a key={l} href={l} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 break-all text-primary hover:underline">
                  <ExternalLink className="h-3.5 w-3.5 shrink-0" /> {l}
                </a>
              ))}
            </div>
          ) : null}
        </Field>
        <Field label="Motivasi">{a.motivation}</Field>

        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-border pt-3 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> Daftar: {fmtDate(record.createdAt)}</span>
          {verified ? (
            <span className="inline-flex items-center gap-1 text-primary"><ShieldCheck className="h-3.5 w-3.5" /> Terverifikasi Turnstile</span>
          ) : (
            <span className="inline-flex items-center gap-1 text-warning"><ShieldAlert className="h-3.5 w-3.5" /> Tanpa verifikasi Turnstile</span>
          )}
          {record.meta?.ip && <span>IP: {record.meta.ip}</span>}
        </div>
      </DialogContent>
    </Dialog>
  );
};
